import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth, ROLES } from '@/contexts/AuthContext';
import { appointmentsService } from '@/services/appointments';
import { patientsService } from '@/services/patients';
import { consultationsService } from '@/services/consultations';
import { getApiErrorMessage } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { AuditLogList } from '@/components/AuditLogList';
import { ArrowLeft, ClipboardText, FloppyDisk } from '@phosphor-icons/react';

const APT_STATUSES = ['Scheduled', 'Completed', 'Cancelled'];

const VISIT_TYPE_LABELS = {
  OPD: 'OPD',
  IPD: 'IPD',
  EMERGENCY: 'Emergency',
};

const CONSULTATION_STATUS_LABELS = {
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

function consultationStatusBadgeClass(status) {
  switch (status) {
    case 'IN_PROGRESS':
      return 'border-primary/50 bg-primary/15 text-primary';
    case 'COMPLETED':
      return 'border-emerald-500/50 bg-emerald-500/15 text-emerald-900 dark:text-emerald-200';
    case 'CANCELLED':
      return 'border-destructive/50 bg-destructive/15 text-destructive';
    default:
      return 'border-muted bg-muted/60 text-muted-foreground';
  }
}

function formatConsultationStatusLabel(status) {
  if (CONSULTATION_STATUS_LABELS[status]) return CONSULTATION_STATUS_LABELS[status];
  if (!status) return '';
  return String(status)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function emptyConsultForm() {
  return {
    visitType: 'OPD',
    status: 'IN_PROGRESS',
    chiefComplaint: { complaint: '', duration: '', severity: '' },
    history: {
      presentIllness: '',
      pastHistory: { diabetes: false, hypertension: false, surgeries: '', other: '' },
      familyHistory: '',
      drugHistory: '',
      allergies: [],
    },
    vitals: {
      temperature: '',
      bloodPressure: '',
      pulse: '',
      respiratoryRate: '',
      spo2: '',
      weight: '',
      height: '',
    },
    examination: {
      general: '',
      cvs: '',
      rs: '',
      cns: '',
      abdomen: '',
      localExamination: '',
    },
    diagnosis: { provisional: '', final: '', icdCode: '' },
    investigations: [{ testName: '', priority: 'ROUTINE', notes: '' }],
    prescriptions: [
      { medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' },
    ],
    advice: '',
    followUp: { date: '', notes: '' },
  };
}

function mergeConsultFromApi(c) {
  if (!c) return { form: emptyConsultForm(), id: null };
  const base = emptyConsultForm();
  const allergies = c.history?.allergies;
  const hist = {
    ...base.history,
    ...(c.history || {}),
    pastHistory: {
      ...base.history.pastHistory,
      ...(c.history?.pastHistory || {}),
    },
  };
  if (Array.isArray(allergies)) {
    hist.allergies = allergies;
  }
  return {
    id: c.id,
    form: {
      visitType: c.visitType || base.visitType,
      status: c.status || base.status,
      chiefComplaint: { ...base.chiefComplaint, ...(c.chiefComplaint || {}) },
      history: hist,
      vitals: { ...base.vitals, ...(c.vitals || {}) },
      examination: { ...base.examination, ...(c.examination || {}) },
      diagnosis: { ...base.diagnosis, ...(c.diagnosis || {}) },
      investigations:
        c.investigations?.length > 0 ? c.investigations : base.investigations,
      prescriptions:
        c.prescriptions?.length > 0 ? c.prescriptions : base.prescriptions,
      advice: c.advice ?? '',
      followUp: { ...base.followUp, ...(c.followUp || {}) },
    },
  };
}

export default function AppointmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLabel, clearLabel } = useBreadcrumb();
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [aptStatus, setAptStatus] = useState('');
  const [consultationId, setConsultationId] = useState(null);
  const [cf, setCf] = useState(emptyConsultForm);
  const [allergiesText, setAllergiesText] = useState('');
  const [consultOpen, setConsultOpen] = useState(false);
  const [auditRefresh, setAuditRefresh] = useState(0);
  const openedConsultFromNavRef = useRef(false);

  const canClinical = hasRole([
    ROLES.DOCTOR,
    ROLES.HOSPITAL_ADMIN,
    ROLES.SUPER_ADMIN,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: apt } = await appointmentsService.getById(id);
      setAppointment(apt);
      setAptStatus(apt.status || 'Scheduled');
      const [{ data: pat }, { data: cons }] = await Promise.all([
        patientsService.getById(apt.patientId),
        consultationsService.getByAppointment(id).catch(() => ({ data: null })),
      ]);
      setPatient(pat);
      const merged = mergeConsultFromApi(cons);
      setConsultationId(merged.id);
      setCf(merged.form);
      const al = merged.form.history?.allergies;
      setAllergiesText(Array.isArray(al) ? al.join(', ') : '');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    openedConsultFromNavRef.current = false;
  }, [id]);

  useEffect(() => {
    if (loading || !appointment) return;
    if (!location.state?.openConsultation || openedConsultFromNavRef.current) return;
    if (!canClinical) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    openedConsultFromNavRef.current = true;
    setConsultOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    loading,
    appointment,
    location.state?.openConsultation,
    canClinical,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    const path = location.pathname;
    if (appointment?.patientName) {
      setLabel(path, appointment.patientName);
    }
    return () => clearLabel(path);
  }, [appointment?.patientName, location.pathname, setLabel, clearLabel]);

  const setField = (path, value) => {
    const parts = path.split('.');
    setCf((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        if (cur[key] == null || typeof cur[key] !== 'object') cur[key] = {};
        cur = cur[key];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const saveAppointmentStatus = async () => {
    try {
      await appointmentsService.update(id, { status: aptStatus });
      toast.success('Appointment status updated');
      const { data } = await appointmentsService.getById(id);
      setAppointment(data);
      setAuditRefresh((v) => v + 1);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const saveConsultation = async () => {
    if (!appointment || !patient) return;
    const allergies = allergiesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const history = {
      ...cf.history,
      allergies,
      pastHistory: cf.history?.pastHistory || {},
    };
    const payload = {
      patientId: appointment.patientId,
      doctorId: appointment.doctorId,
      appointmentId: appointment.id,
      visitDate: new Date().toISOString(),
      visitType: cf.visitType,
      status: cf.status,
      chiefComplaint: cf.chiefComplaint,
      history,
      vitals: cf.vitals,
      examination: cf.examination,
      diagnosis: cf.diagnosis,
      investigations: (cf.investigations || []).filter((x) => x.testName?.trim()),
      prescriptions: (cf.prescriptions || []).filter((x) => x.medicineName?.trim()),
      advice: cf.advice,
      followUp: cf.followUp,
    };
    try {
      if (consultationId) {
        await consultationsService.update(consultationId, payload);
        toast.success('Consultation saved');
      } else {
        const { data } = await consultationsService.create(payload);
        setConsultationId(data.id);
        toast.success('Consultation created');
      }
      setConsultOpen(false);
      await load();
      setAuditRefresh((v) => v + 1);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  if (loading || !appointment) {
    return (
      <div className="space-y-4 max-w-3xl">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="appointment-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/appointments')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-light tracking-tight">Appointment</h1>
          <p className="text-muted-foreground text-sm">
            {formatDate(appointment.date)} · {appointment.time}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Visit</CardTitle>
          <CardDescription>Appointment details and status</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Patient
              </p>
              <p className="font-medium">{appointment.patientName}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Doctor
              </p>
              <p className="font-medium">{appointment.doctorName}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Department
              </p>
              <Badge variant="outline">{appointment.department}</Badge>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Type
              </p>
              <Badge variant="secondary">{appointment.type}</Badge>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end pt-2 border-t">
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label>Appointment status</Label>
              <Select value={aptStatus} onValueChange={setAptStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveAppointmentStatus} className="gap-2">
              <FloppyDisk className="h-4 w-4" />
              Save status
            </Button>
          </div>
        </CardContent>
      </Card>

      <AuditLogList
        entityType="appointment"
        entityId={id}
        title="Appointment activity"
        description="Creates, updates, and status changes for this appointment"
        refreshTrigger={auditRefresh}
      />

      {patient && (
        <Card>
          <CardHeader>
            <CardTitle>Patient basics</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <span className="text-muted-foreground">Name</span>
              <p className="font-medium">{patient.name}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Age / Gender</span>
              <p className="font-medium">
                {patient.age} · {patient.gender}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Phone</span>
              <p className="font-medium">{patient.phone || '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Blood group</span>
              <p className="font-medium">{patient.bloodGroup || '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <span className="text-muted-foreground">Address</span>
              <p className="font-medium">{patient.address || '—'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {canClinical && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Consultation</CardTitle>
              <CardDescription>
                Clinical notes for this visit (linked to this appointment)
              </CardDescription>
            </div>
            <Dialog open={consultOpen} onOpenChange={setConsultOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <ClipboardText className="h-4 w-4" />
                  {consultationId ? 'Edit consultation' : 'Open consultation form'}
                </Button>
              </DialogTrigger>
              <DialogContent
                className={
                  '!flex !h-auto !max-h-[min(88vh,920px)] !w-[calc(100vw-1.5rem)] !max-w-5xl !flex-col !gap-0 ' +
                  '!overflow-hidden !p-0 !shadow-xl sm:!w-[calc(100vw-2rem)] lg:!max-w-6xl'
                }
              >
                <DialogHeader
                  className={
                    'shrink-0 space-y-1.5 border-b bg-background px-4 pb-4 pt-5 text-left sm:px-6 sm:pt-6'
                  }
                >
                  <DialogTitle className="text-xl sm:text-2xl">Consultation</DialogTitle>
                  <DialogDescription className="sr-only">
                    Visit type {VISIT_TYPE_LABELS[cf.visitType] || cf.visitType}. Consultation
                    status {formatConsultationStatusLabel(cf.status)}.
                  </DialogDescription>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span className="text-sm text-muted-foreground">Visit type</span>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-medium',
                        'border-primary/40 bg-primary/10 text-primary',
                      )}
                    >
                      {VISIT_TYPE_LABELS[cf.visitType] || cf.visitType}
                    </span>
                    <span className="text-muted-foreground" aria-hidden>
                      ·
                    </span>
                    <span className="text-sm text-muted-foreground">Status</span>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold',
                        consultationStatusBadgeClass(cf.status),
                      )}
                    >
                      {formatConsultationStatusLabel(cf.status)}
                    </span>
                  </div>
                </DialogHeader>
                <div
                  className={
                    'min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 [scrollbar-gutter:stable] ' +
                    'sm:px-6 lg:px-8 xl:px-10'
                  }
                >
                  <div className="grid grid-cols-1 gap-6 pb-4 pt-2 xl:grid-cols-2 xl:gap-x-10 xl:gap-y-6 xl:pt-4">
                    <div className="min-w-0 space-y-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Visit type</Label>
                        <Select
                          value={cf.visitType}
                          onValueChange={(v) => setField('visitType', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="OPD">OPD</SelectItem>
                            <SelectItem value="IPD">IPD</SelectItem>
                            <SelectItem value="EMERGENCY">Emergency</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Consultation status</Label>
                        <Select
                          value={cf.status}
                          onValueChange={(v) => setField('status', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IN_PROGRESS">In progress</SelectItem>
                            <SelectItem value="COMPLETED">Completed</SelectItem>
                            <SelectItem value="CANCELLED">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Chief complaint</Label>
                      <Input
                        placeholder="Complaint"
                        value={cf.chiefComplaint.complaint}
                        onChange={(e) =>
                          setField('chiefComplaint.complaint', e.target.value)
                        }
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Duration"
                          value={cf.chiefComplaint.duration}
                          onChange={(e) =>
                            setField('chiefComplaint.duration', e.target.value)
                          }
                        />
                        <Input
                          placeholder="Severity"
                          value={cf.chiefComplaint.severity}
                          onChange={(e) =>
                            setField('chiefComplaint.severity', e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Present illness</Label>
                      <Textarea
                        value={cf.history.presentIllness}
                        onChange={(e) =>
                          setField('history.presentIllness', e.target.value)
                        }
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!cf.history.pastHistory?.diabetes}
                          onChange={(e) =>
                            setField('history.pastHistory.diabetes', e.target.checked)
                          }
                        />
                        Diabetes
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!cf.history.pastHistory?.hypertension}
                          onChange={(e) =>
                            setField(
                              'history.pastHistory.hypertension',
                              e.target.checked
                            )
                          }
                        />
                        Hypertension
                      </label>
                    </div>
                    <Input
                      placeholder="Past surgeries"
                      value={cf.history.pastHistory?.surgeries || ''}
                      onChange={(e) =>
                        setField('history.pastHistory.surgeries', e.target.value)
                      }
                    />
                    <Textarea
                      placeholder="Family history"
                      value={cf.history.familyHistory}
                      onChange={(e) =>
                        setField('history.familyHistory', e.target.value)
                      }
                    />
                    <Textarea
                      placeholder="Drug history"
                      value={cf.history.drugHistory}
                      onChange={(e) =>
                        setField('history.drugHistory', e.target.value)
                      }
                    />
                    <Input
                      placeholder="Allergies (comma-separated)"
                      value={allergiesText}
                      onChange={(e) => setAllergiesText(e.target.value)}
                    />
                    </div>

                    <div className="min-w-0 space-y-6">
                    <div className="space-y-2">
                      <Label>Vitals</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          ['temperature', 'Temp °C'],
                          ['bloodPressure', 'BP'],
                          ['pulse', 'Pulse'],
                          ['respiratoryRate', 'RR'],
                          ['spo2', 'SpO2'],
                          ['weight', 'Weight'],
                          ['height', 'Height'],
                        ].map(([k, ph]) => (
                          <Input
                            key={k}
                            placeholder={ph}
                            value={cf.vitals[k] ?? ''}
                            onChange={(e) => setField(`vitals.${k}`, e.target.value)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Examination</Label>
                      {[
                        ['general', 'General'],
                        ['cvs', 'CVS'],
                        ['rs', 'RS'],
                        ['cns', 'CNS'],
                        ['abdomen', 'Abdomen'],
                        ['localExamination', 'Local'],
                      ].map(([k, ph]) => (
                        <Textarea
                          key={k}
                          placeholder={ph}
                          className="min-h-[52px]"
                          value={cf.examination[k] || ''}
                          onChange={(e) =>
                            setField(`examination.${k}`, e.target.value)
                          }
                        />
                      ))}
                    </div>

                    <div className="space-y-2">
                      <Label>Diagnosis</Label>
                      <Input
                        placeholder="Provisional"
                        value={cf.diagnosis.provisional}
                        onChange={(e) =>
                          setField('diagnosis.provisional', e.target.value)
                        }
                      />
                      <Input
                        placeholder="Final"
                        value={cf.diagnosis.final}
                        onChange={(e) =>
                          setField('diagnosis.final', e.target.value)
                        }
                      />
                      <Input
                        placeholder="ICD code"
                        value={cf.diagnosis.icdCode}
                        onChange={(e) =>
                          setField('diagnosis.icdCode', e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Investigations</Label>
                      {cf.investigations.map((inv, i) => (
                        <div key={i} className="flex gap-2 flex-wrap">
                          <Input
                            className="flex-1 min-w-[120px]"
                            placeholder="Test name"
                            value={inv.testName}
                            onChange={(e) => {
                              const next = [...cf.investigations];
                              next[i] = { ...next[i], testName: e.target.value };
                              setField('investigations', next);
                            }}
                          />
                          <Select
                            value={inv.priority || 'ROUTINE'}
                            onValueChange={(v) => {
                              const next = [...cf.investigations];
                              next[i] = { ...next[i], priority: v };
                              setField('investigations', next);
                            }}
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ROUTINE">Routine</SelectItem>
                              <SelectItem value="URGENT">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setField('investigations', [
                            ...cf.investigations,
                            { testName: '', priority: 'ROUTINE', notes: '' },
                          ])
                        }
                      >
                        Add investigation
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>Prescription lines</Label>
                      {cf.prescriptions.map((pr, i) => (
                        <div
                          key={i}
                          className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-md p-2"
                        >
                          <Input
                            placeholder="Medicine"
                            value={pr.medicineName}
                            onChange={(e) => {
                              const next = [...cf.prescriptions];
                              next[i] = { ...next[i], medicineName: e.target.value };
                              setField('prescriptions', next);
                            }}
                          />
                          <Input
                            placeholder="Dosage"
                            value={pr.dosage}
                            onChange={(e) => {
                              const next = [...cf.prescriptions];
                              next[i] = { ...next[i], dosage: e.target.value };
                              setField('prescriptions', next);
                            }}
                          />
                          <Input
                            placeholder="Frequency"
                            value={pr.frequency}
                            onChange={(e) => {
                              const next = [...cf.prescriptions];
                              next[i] = { ...next[i], frequency: e.target.value };
                              setField('prescriptions', next);
                            }}
                          />
                          <Input
                            placeholder="Duration"
                            value={pr.duration}
                            onChange={(e) => {
                              const next = [...cf.prescriptions];
                              next[i] = { ...next[i], duration: e.target.value };
                              setField('prescriptions', next);
                            }}
                          />
                          <Input
                            className="sm:col-span-2"
                            placeholder="Instructions"
                            value={pr.instructions}
                            onChange={(e) => {
                              const next = [...cf.prescriptions];
                              next[i] = { ...next[i], instructions: e.target.value };
                              setField('prescriptions', next);
                            }}
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setField('prescriptions', [
                            ...cf.prescriptions,
                            {
                              medicineName: '',
                              dosage: '',
                              frequency: '',
                              duration: '',
                              instructions: '',
                            },
                          ])
                        }
                      >
                        Add medicine line
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>Advice</Label>
                      <Textarea
                        value={cf.advice}
                        onChange={(e) => setField('advice', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Follow-up date</Label>
                        <Input
                          type="date"
                          value={
                            cf.followUp.date
                              ? String(cf.followUp.date).slice(0, 10)
                              : ''
                          }
                          onChange={(e) =>
                            setField('followUp.date', e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Follow-up notes</Label>
                        <Textarea
                          value={cf.followUp.notes || ''}
                          onChange={(e) =>
                            setField('followUp.notes', e.target.value)
                          }
                        />
                      </div>
                    </div>
                    </div>
                  </div>
                  <div className="mt-6 border-t pt-4">
                    <AuditLogList
                      compact
                      entityType="consultation"
                      entityId={consultationId}
                      title="Consultation activity"
                      description="History of saves and updates for this consultation"
                      refreshTrigger={auditRefresh}
                    />
                  </div>
                </div>
                <div
                  className={
                    'flex shrink-0 justify-end gap-2 border-t bg-background px-4 py-4 sm:px-6'
                  }
                >
                  <Button variant="outline" onClick={() => setConsultOpen(false)}>
                    Close
                  </Button>
                  <Button onClick={saveConsultation}>Save consultation</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          {consultationId && (
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Consultation record #{consultationId.slice(0, 8)}… saved.
              </p>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
